<template>
    <main class="mcp-authorize-page">
        <section class="mcp-authorize-panel">
            <div class="mcp-header">
                <img class="mcp-logo" src="/images/QESG_Assessment_basic.svg" alt="QESG" />
                <div>
                    <div class="mcp-kicker">MCP 연결 승인</div>
                    <h1>AI 에이전트 접근 권한 확인</h1>
                </div>
            </div>

            <b-overlay :show="loading" rounded="sm">
                <div v-if="errorMessage" class="mcp-state mcp-state-error">
                    <fa icon="exclamation-circle" />
                    <p>{{ errorMessage }}</p>
                    <!-- 로그인이 풀렸거나 권한이 없으면 다시 시도해도 같다 — 계정을 바꾸게 하고, 만료된 요청은 앱에서 다시 시작해야 한다 -->
                    <b-button v-if="errorKind === 'auth' || errorKind === 'forbidden'" variant="outline-secondary" size="sm" @click="relogin">
                        다시 로그인
                    </b-button>
                    <b-button v-else-if="errorKind === 'retry'" variant="outline-secondary" size="sm" @click="loadRequest">
                        다시 시도
                    </b-button>
                </div>

                <div v-else-if="authorizationRequest" class="mcp-content">
                    <p class="mcp-description">
                        <template v-if="hasWriteScope">
                            {{ clientName }}에서 QESG 평가 데이터 조회 및 변경 권한을 요청했습니다.
                        </template>
                        <template v-else>
                            {{ clientName }}에서 QESG 평가 데이터 조회를 요청했습니다.
                        </template>
                    </p>

                    <dl class="mcp-detail-list">
                        <div>
                            <dt>요청 앱</dt>
                            <dd>{{ clientName }}</dd>
                        </div>
                        <div>
                            <dt>승인 계정</dt>
                            <dd>
                                {{ accountLabel }}
                                <b-link class="ml-2 f-90" @click="relogin">다른 계정으로 로그인</b-link>
                            </dd>
                        </div>
                        <div>
                            <dt>허용 권한</dt>
                            <dd>
                                <span
                                    v-for="scope in displayScopes"
                                    :key="scope"
                                    class="mcp-scope"
                                    >
                                    {{ scope }}
                                </span>
                            </dd>
                        </div>
                    </dl>

                    <p v-if="hasFullReadScope" class="mcp-note">
                        캠페인 조회와 프로젝트 조회를 함께 허용하면 해당 범위의 문항, 업체 답변, 첨부파일, 평가 결과를 열람할 수 있습니다.
                    </p>
                    <p class="mcp-note">
                        승인한 접근 권한은 만료되지 않으며, 별도로 해제하기 전까지 유지됩니다. 해제가 필요하면 QESG 에 문의해 주세요.
                    </p>

                    <div class="mcp-actions">
                        <b-button
                            variant="outline-secondary"
                            :disabled="approving"
                            @click="cancelRequest"
                            >
                            취소
                        </b-button>
                        <b-button
                            variant="teal"
                            :disabled="approving"
                            @click="approveRequest"
                            >
                            <b-spinner v-if="approving" small class="mr-2" />
                            연결 승인
                        </b-button>
                    </div>
                </div>
            </b-overlay>
        </section>
    </main>
</template>

<script>
import axios from 'axios';
import {tenantKey} from '@shared/utils/tenant';
import { refreshAccessToken, isRefreshableAuthError } from '@core_utils/api/tokenRefresh';

function mcpSubdomainHint () {
    return process.env.VUE_APP_SUBDOMAIN || tenantKey;
}

// 백엔드 app/mcp/oauth_config.py MCP_SCOPES 와 1:1. 매핑이 없으면 scope 원문이 그대로 노출된다 —
// 백엔드에 scope 가 추가되면 여기에도 한 줄 추가할 것.
const SCOPE_LABELS = {
    'mcp:campaigns:read': '캠페인 조회',
    'mcp:projects:read': '프로젝트 조회',
    // seat(자리) = 프로젝트에 발급된 벤더코드 한 건. 삭제는 아무도 가입/답변하지 않은 것만 가능하다
    // (참여 정보나 답변이 있으면 백엔드가 409) — 그 제약이 드러나도록 '미참여' 를 붙인다.
    'mcp:vendor:delete': '미참여 벤더코드 삭제',
    'mcp:service-type:write': '평가 유형(일반/심화) 변경',
    'mcp:test-seat:write': '테스트용 벤더코드 발급'
};

function backendOrigin () {
    return (process.env.VUE_APP_BACKENDHOST || '').replace(/\/api\/?$/, '');
}

function authToken () {
    // 토큰 키 통일: 두 흐름 모두 카멜 'accessToken' 단일 키.
    return localStorage.getItem('accessToken');
}

export default {
    name: 'McpAuthorize',
    data () {
        return {
            authorizationRequest: null,
            loading: false,
            approving: false,
            errorMessage: '',
            // auth(401) / forbidden(403) → 다시 로그인, gone(요청 없음·만료) → 버튼 없음, retry(통신·서버 오류) → 다시 시도
            errorKind: '',
            api: axios.create({
                baseURL: backendOrigin(),
                headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache',
                    'Expires': '0',
                    // 독립 axios 인스턴스라 공용 인터셉터의 X-Subdomain 이 안 붙는다 — 직접 첨부 (백엔드 계약 §1)
                    ...(mcpSubdomainHint() ? { 'X-Subdomain': mcpSubdomainHint() } : {})
                }
            })
        };
    },
    computed: {
        requestId () {
            return this.$route.query.request_id || '';
        },
        accountLabel () {
            const name = localStorage.getItem('user_name');
            const email = localStorage.getItem('e_mail');
            return name && email ? `${name} (${email})` : (name || email || '-');
        },
        clientName () {
            return this.authorizationRequest?.client?.client_name || 'AI 에이전트';
        },
        displayScopes () {
            const scopes = this.authorizationRequest?.scope || [];
            return scopes.map(scope => SCOPE_LABELS[scope] || scope);
        },
        // 백엔드 _has_evaluation_read_scopes: read 2개를 함께 가지면 문항·답변·첨부·평가 결과까지 열린다
        hasFullReadScope () {
            const scopes = this.authorizationRequest?.scope || [];
            return scopes.includes('mcp:campaigns:read') && scopes.includes('mcp:projects:read');
        },
        hasWriteScope () {
            const scopes = this.authorizationRequest?.scope || [];
            return scopes.some(scope => /:(write|delete)$/.test(scope));
        }
    },
    created () {
        // access JWT(15분) 만료로 401 이 오면 refresh 쿠키로 재발급받아 같은 요청을 1회만 재시도한다
        // (_axios.js 인터셉터와 같은 패턴 — _retriedWithRefresh 로 1회 제한). refresh 실패나 재시도 후
        // 401 은 원 에러를 그대로 전파해 화면 안내문만 띄운다. 글로벌 로그아웃 폴백(alert+홈 이동)은
        // 이 페이지에서 쓰지 않는다.
        this.api.interceptors.response.use(undefined, async (error) => {
            const originalRequest = error.config;
            if (error.response?.status === 401 && isRefreshableAuthError(originalRequest)) {
                originalRequest._retriedWithRefresh = true;
                try {
                    const newToken = await refreshAccessToken();
                    originalRequest.headers = originalRequest.headers || {};
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    return this.api(originalRequest);
                } catch (_refreshError) {
                    // refresh 자체 실패(REFRESH_TOKEN_EXPIRED 등) → 아래에서 원 401 그대로 전파
                }
            }
            return Promise.reject(error);
        });
    },
    mounted () {
        this.loadRequest();
    },
    methods: {
        requestConfig () {
            return {
                headers: {
                    Authorization: `Bearer ${authToken()}`
                }
            };
        },
        setError (kind, message) {
            this.errorKind = kind;
            this.errorMessage = message;
        },
        async loadRequest () {
            if (!this.requestId) {
                this.setError('gone', '승인 요청 정보가 없습니다. 연결을 요청한 앱에서 다시 시작해 주세요.');
                return;
            }

            this.loading = true;
            this.setError('', '');
            try {
                const response = await this.api.get(
                    `/oauth/authorization-requests/${encodeURIComponent(this.requestId)}`,
                    this.requestConfig()
                );
                this.authorizationRequest = response.data;
            } catch (error) {
                const status = error.response?.status;
                if (status === 401) {
                    this.setError('auth', '로그인이 필요합니다. 다시 로그인한 뒤 연결을 승인해 주세요.');
                } else if (status === 403) {
                    // 백엔드 _consent_gate: 관리자 등급이 아니면 403 access_denied
                    this.setError('forbidden', '관리자 계정만 연결을 승인할 수 있습니다.');
                } else if (status === 404) {
                    this.setError('gone', '만료되었거나 찾을 수 없는 승인 요청입니다. 연결을 요청한 앱에서 다시 시작해 주세요.');
                } else {
                    this.setError('retry', '승인 요청을 불러오지 못했습니다.');
                }
            } finally {
                this.loading = false;
            }
        },
        async approveRequest () {
            this.approving = true;
            this.setError('', '');
            try {
                const response = await this.api.post(
                    `/oauth/authorization-requests/${encodeURIComponent(this.requestId)}/approve`,
                    null,
                    this.requestConfig()
                );
                // 성공 시 페이지를 떠나는 동안 버튼은 비활성으로 둔다 (더블클릭 → 두 번째 approve 400 방지)
                window.location.assign(response.data.redirect_uri);
            } catch (error) {
                const status = error.response?.status;
                if (status === 401) {
                    this.setError('auth', '로그인 세션이 만료되었습니다. 다시 로그인해 주세요.');
                } else if (status === 403) {
                    this.setError('forbidden', '관리자 계정만 연결을 승인할 수 있습니다.');
                } else {
                    this.setError('retry', '연결 승인에 실패했습니다.');
                }
                this.approving = false;
            }
        },
        // 로그인 화면은 토큰이 남아 있으면 이 화면으로 되돌려 보내므로 먼저 로그아웃한다.
        // logout 액션은 서버 호출이 실패해도(토큰 만료 401 포함) 로컬 세션·캐시를 모두 정리하고 throw 하지 않는다.
        async relogin () {
            await this.$store.dispatch('logout');
            this.$router.replace({ name: 'Login', query: { redirect: this.$route.fullPath } });
        },
        cancelRequest () {
            // 이 페이지는 커넥터(Claude 등)에서 넘어오므로, 취소는 홈이 아니라 뒤로 가야
            // 호출한 앱 화면으로 돌아간다. 히스토리가 없으면(주소 직접 입력·새 탭) 홈으로.
            if (window.history.length > 1) {
                this.$router.back();
            } else {
                this.$router.push({ name: 'Main' });
            }
        }
    }
};
</script>

<style scoped>
.mcp-authorize-page {
    min-height: 100vh;
    background: #f6f8fb;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px 16px;
}

.mcp-authorize-panel {
    width: min(100%, 560px);
    background: #fff;
    border: 1px solid #e4e8ef;
    border-radius: 8px;
    box-shadow: 0 16px 48px rgba(25, 34, 50, 0.08);
    padding: 32px;
}

.mcp-header {
    display: flex;
    gap: 16px;
    align-items: center;
    margin-bottom: 28px;
}

.mcp-logo {
    width: 88px;
    height: auto;
}

.mcp-kicker {
    color: #377d71;
    font-size: 0.875rem;
    font-weight: 700;
    margin-bottom: 4px;
}

.mcp-header h1 {
    font-size: 1.5rem;
    line-height: 1.35;
    margin: 0;
    color: #18202c;
}

.mcp-description {
    color: #3a4655;
    margin-bottom: 24px;
}

.mcp-detail-list {
    border-top: 1px solid #edf0f5;
    border-bottom: 1px solid #edf0f5;
    margin: 0 0 28px;
}

.mcp-detail-list > div {
    display: grid;
    grid-template-columns: 120px 1fr;
    gap: 16px;
    padding: 16px 0;
}

.mcp-detail-list > div + div {
    border-top: 1px solid #edf0f5;
}

.mcp-detail-list dt {
    color: #6c7888;
    font-weight: 600;
}

.mcp-detail-list dd {
    margin: 0;
    color: #18202c;
}

.mcp-scope {
    display: inline-flex;
    align-items: center;
    min-height: 28px;
    padding: 4px 10px;
    margin: 0 6px 6px 0;
    border-radius: 999px;
    background: #edf7f4;
    color: #24685f;
    font-size: 0.875rem;
    font-weight: 600;
}

.mcp-note {
    color: #5b6675;
    font-size: 0.875rem;
    line-height: 1.5;
    margin: -12px 0 16px;
}

.mcp-note + .mcp-note {
    margin-top: 0;
}

.mcp-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

.mcp-state {
    min-height: 180px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    color: #5b6675;
    text-align: center;
}

.mcp-state-error {
    color: #a23a3a;
}

@media (max-width: 576px) {
    .mcp-authorize-panel {
        padding: 24px;
    }

    .mcp-header {
        align-items: flex-start;
        flex-direction: column;
    }

    .mcp-detail-list > div {
        grid-template-columns: 1fr;
        gap: 6px;
    }

    .mcp-actions {
        flex-direction: column-reverse;
    }

    .mcp-actions .btn {
        width: 100%;
    }
}
</style>
